import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PackageIcon, EyeIcon, UsersIcon, TrendingUpIcon, ArrowRightIcon, PlusIcon, BotIcon, LayersIcon, CarIcon, Share2Icon } from 'lucide-react';
import { useBusiness, getHubTabs } from './BusinessContext';
interface BusinessDashboardProps {
  onNavigate?: (tab: string) => void;
}
export function BusinessDashboard({
  onNavigate
}: BusinessDashboardProps) {
  const {
    hub
  } = useBusiness();
  const tabs = getHubTabs(hub?.type ?? 'generic');
  const inventoryLabel = tabs[1];
  const businessName = hub?.name || 'there';
  // Mock Data organized around user categories
  const categories = [{
    name: 'SUVs',
    listings: 42,
    views: '12.4k',
    leads: 156,
    trend: '+14%'
  }, {
    name: 'Sedans',
    listings: 28,
    views: '8.2k',
    leads: 89,
    trend: '+5%'
  }, {
    name: 'Hatchbacks',
    listings: 15,
    views: '3.1k',
    leads: 24,
    trend: '-2%'
  }];
  const kpis = [{
    label: `Total ${inventoryLabel}`,
    value: '85',
    trend: '+4'
  }, {
    label: 'Total Views',
    value: '23.7k',
    trend: '+12%'
  }, {
    label: 'New Leads',
    value: '269',
    trend: '+8%'
  }, {
    label: 'Conversion',
    value: '3.2%',
    trend: '+0.4%'
  }];
  const topPerforming = [{
    id: 1,
    title: 'Toyota Prado GXR 2023',
    category: 'SUVs',
    views: '1.2k',
    leads: 45,
    img: 'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=200'
  }, {
    id: 2,
    title: 'Nissan Patrol V8 2022',
    category: 'SUVs',
    views: '980',
    leads: 38,
    img: 'https://images.unsplash.com/photo-1580273916550-e323be2ae537?auto=format&fit=crop&q=80&w=200'
  }, {
    id: 3,
    title: 'Honda Accord Sport 2023',
    category: 'Sedans',
    views: '850',
    leads: 29,
    img: 'https://images.unsplash.com/photo-1606152421802-db97b9c7a11b?auto=format&fit=crop&q=80&w=200'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 15
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24
      }
    }
  };
  // Graceful Empty State
  if (categories.length === 0) {
    return <motion.div variants={containerVariants} initial="hidden" animate="show" className="max-w-3xl mx-auto text-center py-24 px-4" data-id="element-565" data-mp="1fm5s9gz0go9o">
        <motion.div variants={itemVariants} className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-8 border border-slate-100 shadow-sm" data-id="element-566" data-mp="1oncbhxt1od6m">
          <PackageIcon className="w-10 h-10 text-slate-300" data-id="element-567" data-mp="vzpyjyxxfw73" />
        </motion.div>
        <motion.h2 variants={itemVariants} className="font-display font-bold text-3xl text-dark-navy mb-4" data-id="element-568" data-mp="37mjq1jtlydrm">
          Welcome to your Business Hub
        </motion.h2>
        <motion.p variants={itemVariants} className="text-slate-500 text-lg mb-10 max-w-lg mx-auto leading-relaxed" data-id="element-569" data-mp="2yny7vps1t0rd">
          Your hub is ready. Add your first {inventoryLabel.toLowerCase()} to
          start receiving leads and let Talab AI handle customer inquiries.
        </motion.p>
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-570" data-mp="3vc4j4vjh6rem">
          <button onClick={() => onNavigate?.(inventoryLabel)} className="w-full sm:w-auto px-6 py-3.5 bg-primary-indigo text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-571" data-mp="3mbpwnsurz69j">
            <PlusIcon className="w-5 h-5" data-id="element-572" data-mp="2q6pjczc1fofm" /> Add {inventoryLabel}
          </button>
          <button onClick={() => onNavigate?.('Settings')} className="w-full sm:w-auto px-6 py-3.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-573" data-mp="2q8tspwsphfna">
            <Share2Icon className="w-5 h-5 text-slate-400" data-id="element-574" data-mp="2cd80lgr6efqn" /> Share Shop Link
          </button>
        </motion.div>
      </motion.div>;
  }
  // Active Populated State
  return <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-10 pb-12 max-w-6xl mx-auto" data-id="element-575" data-mp="354hvdhidlup2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-576" data-mp="2v4r1ni1n3jq1">
        <div data-id="element-577" data-mp="1oq067tvacr3e">
          <h2 className="font-display font-bold text-3xl text-dark-navy mb-2" data-id="element-578" data-mp="1oqro61vymqb7">
            Good Morning, {businessName}
          </h2>
          <p className="text-slate-500 text-lg" data-id="element-579" data-mp="1jsq7fnm0b32w">
            Here's an overview of your {inventoryLabel.toLowerCase()}{' '}
            performance.
          </p>
        </div>
      </div>

      {/* KPI Strip */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-id="element-580" data-mp="1ejgq7nmxw0h5">
        {kpis.map(kpi => <div key={kpi.label} className="bg-white rounded-3xl p-6 border border-slate-200/60 shadow-sm flex flex-col justify-between" data-id="element-581" data-mp="1ocefqdqcmpp9">
            <p className="text-sm font-medium text-slate-500 mb-3" data-id="element-582" data-mp="1xbrnn1ytr67v">
              {kpi.label}
            </p>
            <div className="flex items-end gap-3" data-id="element-583" data-mp="22b4c1r5cglck">
              <p className="text-3xl font-display font-bold text-dark-navy leading-none tracking-tight" data-id="element-584" data-mp="229jgpzb7gydn">
                {kpi.value}
              </p>
              {kpi.trend && <span className={`text-xs font-bold px-2 py-1 rounded-md mb-0.5 ${kpi.trend.startsWith('+') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-585" data-mp="7z7dzfcech8j">
                  {kpi.trend}
                </span>}
            </div>
          </div>)}
      </motion.div>

      {/* Categories Section */}
      <motion.div variants={itemVariants} data-id="element-586" data-mp="euz6q1fx1c1b">
        <div className="flex items-center justify-between mb-6" data-id="element-587" data-mp="eruvwl7y3i1o">
          <h3 className="font-display font-bold text-xl text-dark-navy flex items-center gap-2" data-id="element-588" data-mp="9u1sfzy11253">
            <LayersIcon className="w-5 h-5 text-primary-indigo" data-id="element-589" data-mp="ntb6ou2o9m6f" /> Your
            Categories
          </h3>
          <button onClick={() => onNavigate?.(inventoryLabel)} className="text-sm font-semibold text-primary-indigo hover:text-indigo-700 flex items-center gap-1 transition-colors" data-id="element-590" data-mp="iuzwgi8gt9no">
            View all <ArrowRightIcon className="w-4 h-4" data-id="element-591" data-mp="snouaab3ep0j" />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-592" data-mp="16ldgblgwc276">
          {categories.map(cat => <button key={cat.name} onClick={() => onNavigate?.(inventoryLabel)} className="text-left bg-white rounded-3xl p-6 border border-slate-200/60 shadow-sm hover:border-primary-indigo/30 hover:shadow-md transition-all group flex flex-col h-full" data-id="element-593" data-mp="12ld13ylz3mz2">
              <div className="flex items-start justify-between mb-6" data-id="element-594" data-mp="1c98nwa8rid6c">
                <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center group-hover:bg-primary-indigo group-hover:text-white transition-colors" data-id="element-595" data-mp="1h4nwamo03gvd">
                  <CarIcon className="w-6 h-6 text-primary-indigo group-hover:text-white transition-colors" data-id="element-596" data-mp="178b2zpwdb515" />
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-md ${cat.trend.startsWith('+') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-597" data-mp="ti7pbnbk0jnb">
                  {cat.trend}
                </span>
              </div>
              <h4 className="font-bold text-lg text-dark-navy mb-1" data-id="element-598" data-mp="ohtha59yd9pa">
                {cat.name}
              </h4>
              <p className="text-sm text-slate-500 mb-6" data-id="element-599" data-mp="13y9wy8lvpkms">
                {cat.listings} active listings
              </p>

              <div className="mt-auto grid grid-cols-2 gap-4 pt-4 border-t border-slate-100" data-id="element-600" data-mp="z19rnzwuslwq">
                <div data-id="element-601" data-mp="ux1zfsr6sntr">
                  <p className="text-xs font-medium text-slate-400 mb-1 flex items-center gap-1" data-id="element-602" data-mp="v0ppuj1c4u49">
                    <EyeIcon className="w-3.5 h-3.5" data-id="element-603" data-mp="q24twvt48wlm" /> Views
                  </p>
                  <p className="font-bold text-slate-700 text-base" data-id="element-604" data-mp="l2s4q4ervdfv">
                    {cat.views}
                  </p>
                </div>
                <div data-id="element-605" data-mp="bae1t8byk0s4">
                  <p className="text-xs font-medium text-slate-400 mb-1 flex items-center gap-1" data-id="element-606" data-mp="bbp5771hr6ip">
                    <UsersIcon className="w-3.5 h-3.5" data-id="element-607" data-mp="7aloyjfj78gy" /> Leads
                  </p>
                  <p className="font-bold text-slate-700 text-base" data-id="element-608" data-mp="2ah7or6su9po">
                    {cat.leads}
                  </p>
                </div>
              </div>
            </button>)}
        </div>
      </motion.div>

      {/* Top Performing */}
      <motion.div variants={itemVariants} data-id="element-609" data-mp="290u9hdfmu4v0">
        <h3 className="font-display font-bold text-xl text-dark-navy mb-6 flex items-center gap-2" data-id="element-610" data-mp="2dwro9fb8z35o">
          <TrendingUpIcon className="w-5 h-5 text-primary-indigo" data-id="element-611" data-mp="2ixp7zwbwo59a" /> Top
          Performing
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-612" data-mp="2mx7tat7n2icn">
          {topPerforming.map(item => <button key={item.id} onClick={() => onNavigate?.(inventoryLabel)} className="text-left bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm flex gap-4 items-center hover:border-primary-indigo/30 hover:shadow-md transition-all group" data-id="element-613" data-mp="1q9te0zdq0rg7">
              <div className="w-20 h-20 bg-slate-100 rounded-xl flex-shrink-0 overflow-hidden" data-id="element-614" data-mp="241hnsw0hz3u0">
                <img src={item.img} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-615" data-mp="17fee97jl4urb" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-616" data-mp="n4s3qbon6ufu">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block" data-id="element-617" data-mp="n3go9elu3y8w">
                  {item.category}
                </span>
                <h4 className="font-bold text-sm text-dark-navy truncate mb-2 group-hover:text-primary-indigo transition-colors" data-id="element-618" data-mp="w683zqlqtg6c">
                  {item.title}
                </h4>
                <div className="flex items-center gap-3 text-xs font-medium text-slate-500" data-id="element-619" data-mp="3h134kytgehx">
                  <span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-md" data-id="element-620" data-mp="8gnhixqds0sb">
                    <EyeIcon className="w-3.5 h-3.5" data-id="element-621" data-mp="d6u4kt58vd8g" /> {item.views}
                  </span>
                  <span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-md" data-id="element-622" data-mp="3bx4304josyvp">
                    <UsersIcon className="w-3.5 h-3.5" data-id="element-623" data-mp="3bv9ob7z0e302" /> {item.leads}
                  </span>
                </div>
              </div>
            </button>)}
        </div>
      </motion.div>
    </motion.div>;
}